/**
 * NotificationsBell — header bell + popover inbox.
 *
 * Shows the unread count as a badge over the bell icon; clicking opens a
 * popover with the most recent 20 notifications. Each item is a single
 * tappable row that marks-as-read on click and navigates to the related
 * demand (if `related_id` is set). "Mark all read" iterates the existing
 * single-row mutation — fine while N is small.
 *
 * The Sidebar's 60s-polling notification query is the canonical refresh
 * source for the badge; this component reads its own copy via
 * `useNotifications` (also 60s polling) so the badge stays in sync after
 * mark-as-read mutations without bouncing.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatRelative } from '../lib/relativeTime'
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Alert24Regular } from '@fluentui/react-icons'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../hooks/useNotifications'
import { useToast } from './ToastProvider'
import type { NotificationItem } from '../types/notification'
import { bayerColors, bucketColors } from '../theme'
import { apiErrorMessage } from '../lib/apiError'

const useStyles = makeStyles({
  triggerWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: '16px',
    height: '16px',
    padding: '0 4px',
    borderRadius: '999px',
    background: bucketColors.highPriority,
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '16px',
    textAlign: 'center',
    pointerEvents: 'none',
    boxShadow: '0 0 0 2px #fff',
  },
  surface: {
    width: '360px',
    maxHeight: '480px',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    transitionProperty: 'background',
    transitionDuration: '100ms',
    ':hover': {
      background: tokens.colorNeutralBackground2,
    },
  },
  itemUnread: {
    background: bayerColors.navyLight,
    ':hover': {
      background: '#dceaf6',
    },
  },
  itemMsg: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.4,
  },
  itemMsgUnread: {
    fontWeight: 600,
  },
  itemMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  empty: {
    padding: tokens.spacingHorizontalXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
})

export function NotificationsBell() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { data, isLoading, isError, refetch } = useNotifications(false)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const { showToast } = useToast()

  const items: NotificationItem[] = data?.items ?? []
  const unread = data?.unread ?? 0
  const badgeText = useMemo(() => (unread > 99 ? '99+' : String(unread)), [unread])

  function handleItemClick(n: NotificationItem) {
    if (!n.read) {
      markRead.mutate(n.id, {
        onError: (err) =>
          showToast({ title: "Couldn't mark as read", body: apiErrorMessage(err), intent: 'error' }),
      })
    }
    setOpen(false)
    // Prefer an explicit deep link (e.g. "/tickets/12"); fall back to the demand.
    if (n.link) navigate(n.link)
    else if (n.related_id) navigate(`/demand/${n.related_id}`)
  }

  function handleMarkAllRead() {
    // One server call clears every unread — including any beyond the loaded ≤50
    // — so the badge always reaches zero.
    markAll.mutate(undefined, {
      onError: (err) =>
        showToast({ title: "Couldn't mark all read", body: apiErrorMessage(err), intent: 'error' }),
    })
  }

  // The Button itself is the trigger. It used to be a <span> wrapping the
  // button and the badge, which the trigger turned into a second button —
  // one control, two tab stops, a button inside a button (#1385). The badge
  // is decoration beside it; the unread count is already in the label.
  return (
    <span className={styles.triggerWrap}>
      <Popover
        open={open}
        onOpenChange={(_, d) => setOpen(d.open)}
        positioning="below-end"
        trapFocus
      >
        <PopoverTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            icon={<Alert24Regular />}
            aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
            data-testid="notifications-bell"
          />
        </PopoverTrigger>
        <PopoverSurface className={styles.surface}>
          <div className={styles.header}>
            <span className={styles.title}>Notifications</span>
            {unread > 0 && (
              <Button
                appearance="subtle"
                size="small"
                onClick={handleMarkAllRead}
                data-testid="notifications-mark-all-read"
              >
                Mark all read
              </Button>
            )}
          </div>
          <div className={styles.list}>
            {isLoading ? (
              <div className={styles.loading}>
                <Spinner size="tiny" />
              </div>
            ) : isError ? (
              <div className={styles.empty} role="alert" data-testid="notifications-error">
                Couldn&apos;t load notifications.
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    appearance="secondary"
                    onClick={() => {
                      void refetch()
                    }}
                    data-testid="notifications-retry"
                  >
                    Try again
                  </Button>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className={styles.empty} data-testid="notifications-empty">
                No notifications yet.
              </div>
            ) : (
              items.slice(0, 20).map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={
                    n.read
                      ? styles.item
                      : `${styles.item} ${styles.itemUnread}`
                  }
                  onClick={() => handleItemClick(n)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <span
                    className={
                      n.read ? styles.itemMsg : `${styles.itemMsg} ${styles.itemMsgUnread}`
                    }
                  >
                    {n.message}
                  </span>
                  <span className={styles.itemMeta}>{formatRelative(n.created_at)}</span>
                </button>
              ))
            )}
          </div>
        </PopoverSurface>
      </Popover>
      {unread > 0 && (
        <span className={styles.badge} aria-hidden="true">
          {badgeText}
        </span>
      )}
    </span>
  )
}
